import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {useParams, useHistory, useLocation} from "react-router-dom";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import CreatableSelect from "react-select/creatable";
import useAxios from "../../utils/useAxios";
import TournamentGroups from "./groups/TournamentGroups";
import TournamentGrids from "./draws/TournamentGrids";
import TournamentDrawStages from "./draws/TournamentDrawStages";
import TournamentPlayers from "./players/TournamentPlayers";
import TournamentSchedule from "./schedule/TournamentSchedule";
import TournamentRatingTab from "./rating/TournamentRatingTab";
import TournamentInformationForm from "./info/TournamentInformationForm";
import TournamentResults from "./TournamentResults";
import { tournamentCache, tournamentSubscribers } from "../../utils/tournamentCache";
import { refreshSubscribers } from "../../utils/tournamentRefreshBus";
import { publishTournamentSearch } from "../../utils/tournamentSearchBus";
import { tournamentSearchOptionsSubscribers } from "../../utils/tournamentSearchOptionsBus";
import { getVisibleTabs } from "./TournamentHeader";
import TournamentSwipeTabs from "../../components/TournamentSwipeTabs";
import { Helmet } from "react-helmet-async";
import { isPreviewFrame } from "../../utils/previewFrame";

const Tournament = ({ is_authorized = true }) => {
    const { id } = useParams();
    const location = useLocation();
    const history = useHistory();
    const api = useAxios();
    const { t } = useTranslation();
    // One search box for the whole tournament page, replacing what used to
    // be a separate search icon/input duplicated in each tab (Участники/
    // Жеребкування/Розклад/Рейтинг) - lives here, not in any tab, because
    // renderTabContent() below fully unmounts/remounts the active tab on
    // every ?tab= switch (see its own key={refreshKey} comment), so state
    // held inside a tab can't survive switching away and back. The actual
    // icon that toggles this lives in TournamentHeader.js instead (a
    // separate React tree mounted by Header.js at the App.js level, outside
    // this page entirely) - tournamentSearchBus is the only channel between
    // the two, same pattern TournamentHeader.js already uses for
    // tournamentHeroBus.
    //
    // Chips, not a plain string - same CreatableSelect-with-chips look and
    // behavior as Розклад's own search (ScheduleTable.js), instead of the
    // plain text input this used to be. Chip *options* (player names, group
    // letters, bracket places) come from whichever tab is actually mounted -
    // it has the data, this component doesn't - published up through
    // tournamentSearchOptionsBus, the same cross-tree pattern already used
    // for the open/close toggle above.
    const [searchOpen, setSearchOpen] = useState(false);
    // --tournament-header-h (see Header.js) is a live ResizeObserver
    // reading, not a constant - it also moves during the hero-collapse
    // crossfade (useTournamentHeroCollapse) as the header's title fades in
    // while scrolling. This search row's own `top` used that var directly,
    // so scrolling with the search open visibly slid the row up/down
    // chasing the header's own height instead of staying put. Snapshotting
    // the value once, right as the row opens, and pinning `top` to that
    // fixed number instead keeps it still regardless of what the header
    // does afterward.
    const [searchTopPx, setSearchTopPx] = useState(null);
    const [searchChips, setSearchChips] = useState([]);
    // Purely for the input's own controlled display/typing (and react-
    // select's own default option filtering) - NOT fed into matching.
    // Highlighting/dim/scroll only ever reacts to committed chips
    // (searchChips itself), same as Розклад - a per-keystroke version of
    // this was tried and reverted (felt chaotic mid-typing, and briefly
    // wiped results when interacting with an existing chip).
    const [searchInputValue, setSearchInputValue] = useState("");
    const [searchOptions, setSearchOptions] = useState([]);
    const toggleSearch = useCallback(() => {
        setSearchOpen(prev => {
            const next = !prev;
            if (next) {
                const raw = getComputedStyle(document.documentElement).getPropertyValue('--tournament-header-h');
                setSearchTopPx(raw ? raw.trim() : null);
            } else {
                setSearchChips([]);
                setSearchInputValue("");
                setSearchTopPx(null);
            }
            return next;
        });
    }, []);
    useEffect(() => {
        publishTournamentSearch({ isOpen: searchOpen, onToggle: toggleSearch });
        return () => publishTournamentSearch(null);
    }, [searchOpen, toggleSearch]);
    useEffect(() => {
        const handler = (options) => setSearchOptions(options || []);
        tournamentSearchOptionsSubscribers.add(handler);
        return () => tournamentSearchOptionsSubscribers.delete(handler);
    }, []);
    // A cached tournament fetched in public view has can_edit:false baked in.
    // Trusting it as-is when mounting into admin mode would broadcast that
    // stale, wrong-permission object to subscribers (e.g. Header's bottom
    // nav) before the authorized refetch below resolves - flashing the
    // public/user nav for a moment. Only seed from cache when it actually
    // matches the mode we're mounting into.
    const cachedMatchesMode = tournamentCache[id] && (!is_authorized || tournamentCache[id].can_edit);
    const [tournament, setTournament] = useState(cachedMatchesMode ? tournamentCache[id] : null);
    const [loading, setLoading] = useState(!cachedMatchesMode);
    // Lazy initializer (not a hardcoded "information" corrected a tick later
    // by the effect below) - reading the real ?tab= param straight from the
    // URL on the very first render is what stops every single mount of this
    // component (a hard page reload, admin/public toggle, any time this
    // gets remounted) from unconditionally flashing Information first and
    // only then jumping to whatever tab the URL actually said, regardless
    // of mode.
    const [activeTab, setActiveTab] = useState(() => new URLSearchParams(location.search).get("tab") || "information");
    const [hasGuestToken, setHasGuestToken] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    // A previous tab's options (e.g. Жеребкування's group/bracket chips)
    // must not linger once its own tab unmounts - the newly-mounted tab
    // re-publishes its own set right away, but only once its own data has
    // loaded, so without this reset there'd be a window (and, for tabs that
    // never publish anything, forever) where a stale, no-longer-relevant set
    // of options is still offered.
    useEffect(() => {
        setSearchOptions([]);
    }, [activeTab]);
    // Guards fetchTournament against out-of-order responses: the mount
    // effect fires one, and every settings save/reset (via
    // requestTournamentRefresh) fires another - without this, a slower
    // pre-change response can land after the newer one and silently
    // overwrite the freshly-changed tournament with the old one.
    const tournamentReqRef = useRef(0);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get("tab");
        if (tab) {
            setActiveTab(tab);
        } else {
            setActiveTab("information");
        }
    }, [location.search]);

    const fetchTournament = (forced = false) => {
        const thisReq = ++tournamentReqRef.current;
        // Don't show full-page loading if we already have tournament data (e.g. background refresh)
        if (!tournament || forced) {
            setLoading(true);
        }
        api.get(`/api/v1.0/tournaments/${id}/`)
            .then(res => {
                if (thisReq !== tournamentReqRef.current) return;
                tournamentCache[id] = res.data;
                setTournament(res.data);
                tournamentSubscribers.forEach(cb => cb(id, res.data));
            })
            .catch(error => {
                if (thisReq !== tournamentReqRef.current) return;
                console.error(error);
            })
            .finally(() => {
                if (thisReq === tournamentReqRef.current) {
                    setLoading(false);
                }
            });
    };

    // Refetch whenever the tournament ID changes, or whenever a settings
    // modal reports a change / reset via requestTournamentRefresh
    useEffect(() => {
        fetchTournament();
    }, [id, refreshKey]);

    useEffect(() => {
        const refreshHandler = (targetId) => {
            if (!targetId || targetId === id) {
                setRefreshKey(k => k + 1);
            }
        };
        refreshSubscribers.add(refreshHandler);
        return () => {
            refreshSubscribers.delete(refreshHandler);
        };
    }, [id]);

    useEffect(() => {
        const token = localStorage.getItem("guest_access_token");
        if (token) {
            setHasGuestToken(true);
        }
    }, []);

    // Plain setTournament only updates this page's own local state - the
    // sticky header (components/Header.js) keeps a separate copy fed by
    // tournamentSubscribers, which otherwise only fires on the initial
    // fetch above. Without also syncing the cache/subscribers here, an
    // in-place edit (e.g. renaming in the Information tab) updated this
    // page but left the header showing the stale name.
    const updateTournament = (updater) => {
        setTournament(prev => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            if (next) {
                tournamentCache[id] = next;
                tournamentSubscribers.forEach(cb => cb(id, next));
            }
            return next;
        });
    };

    const renderTabContent = () => {
        const isSuperAdmin = localStorage.getItem("is_super_admin") === "true";
        // Before the tournament fetch resolves (or when a stale cache from a
        // different mode was deliberately not seeded - see cachedMatchesMode
        // above), tournament?.can_edit/can_write_in_base are simply unknown,
        // not false - but treating "unknown" as false made every admin-mode
        // navigation without a warm matching cache render the public/
        // read-only tab first and then jump to the editable one once the
        // fetch landed. `is_authorized` (route + login, already resolved
        // synchronously - see App.js) is the correct optimistic guess for
        // that brief window: right for the overwhelmingly common case (an
        // admin opening their own tournament), and self-corrects the moment
        // real data arrives on the rare case it guessed wrong (a shared
        // admin link to a tournament this user doesn't actually own).
        const canWrite = (loading ? is_authorized : tournament?.can_write_in_base) || isSuperAdmin;
        const canEdit = ((loading ? is_authorized : tournament?.can_edit) && !hasGuestToken) || isSuperAdmin;

        const props = {
            tournament,
            setTournament: updateTournament,
            loading,
            is_authorized: is_authorized && canEdit,
            can_write_in_base: canWrite,
            refreshKey,
            searchChips
        };

        // key={refreshKey}: each tab's own stage list (useFetchStages) only
        // ever fetches once, on its own mount - refreshKey being passed down
        // as a plain prop did nothing to make Groups/Grids/Schedule notice a
        // settings change (a newly added stage, a reset, etc.) short of a
        // full page reload. Forcing a remount here is what actually gets
        // them to refetch, matching what useFetchStages' own comment already
        // assumed was happening.
        switch (activeTab) {
            case "information": return <TournamentInformationForm key={refreshKey} {...props} />;
            // Kept for TournamentExportModal.js's hidden-iframe PDF/PNG export,
            // which still loads ?tab=groups / ?tab=brackets standalone to
            // screenshot #groups-block / #brackets-block one at a time - not
            // reachable from the header nav anymore (see "draw-stages" below).
            case "groups": return <TournamentGroups key={refreshKey} {...props} />;
            case "brackets":
            case "draws": return <TournamentGrids key={refreshKey} {...props} />;
            case "draw-stages": return <TournamentDrawStages key={refreshKey} {...props} />;
            case "results": return <TournamentResults key={refreshKey} {...props} />;
            case "players": return <TournamentPlayers key={refreshKey} {...props} />;
            case "schedule": return <TournamentSchedule key={refreshKey} {...props} />;
            // TournamentRatingTab folds both rating "views" behind one
            // sub-tab bar: очки турніру (TournamentRating, this tournament's
            // own results - still also reachable as its own sub-tab inside
            // "players" above) and the full/overall standings (RatingTable,
            // same component the admin's standalone /admin/ratings/:id page
            // and the rating-badge modal use).
            case "rating": return <TournamentRatingTab key={refreshKey} {...props} />;
            default: return <div>Tab not found</div>;
        }
    };

    const visibleTabs = useMemo(() => getVisibleTabs(tournament, is_authorized), [tournament, is_authorized]);

    const ogImage = tournament?.cover_url || tournament?.logo || tournament?.image || "";

    // .page_container's own padding-bottom (index.css:9831/9951) is what
    // clears the fixed bottom nav everywhere else - it's keyed off a real
    // .main-header sibling that only exists in the actual outer page.
    // Inside the "Screen size" preview iframe, Header (and with it
    // .main-header) is deliberately never mounted (see App.js), so that
    // selector never matches there and this content would otherwise butt
    // flush against the framed viewport's own bottom edge - overlapping the
    // OUTER page's real, still-fixed bottom nav sitting visually on top of
    // it (Жеребкування) or leaving nothing to smooth the size jump when a
    // short tab (Розклад) ends well above it. Matching the same 64px here
    // for the framed copy keeps every tab's own bottom clearance identical
    // to what a real, non-previewed visit already gets.
    const previewFramePadding = isPreviewFrame() ? { paddingBottom: "64px" } : null;

    return (
        <Container className="page_container" style={{ marginTop: "-2px", ...previewFramePadding }}>
            <Helmet>
                {tournament?.name && <meta property="og:title" content={tournament.name} />}
                <meta property="og:description" content={tournament?.name ? `Приєднуйтесь до турніру ${tournament.name}!` : "Приєднуйтесь до турніру!"} />
                {ogImage && <meta property="og:image" content={ogImage} />}
            </Helmet>
            {searchOpen && (
                <ul
                    className="tabs"
                    style={{
                        marginTop: "8px",
                        marginBottom: "5px",
                        flexWrap: "wrap",
                        // Pins right under the tournament header (its own
                        // height varies with the name wrapping/tabs row, so
                        // --tournament-header-h - set by Header.js off the
                        // real measured height - not a guessed constant, same
                        // var .month-tabs-wrap uses for the same reason on
                        // the tournaments list).
                        position: "sticky",
                        top: searchTopPx || "var(--tournament-header-h)",
                        zIndex: 14,
                        background: "var(--body-background)"
                    }}
                >
                    <li style={{ flex: "1 1 200px" }}>
                        {/* Same chip-based CreatableSelect look/behavior as
                            Розклад's own search (ScheduleTable.js) - options
                            come from whichever tab is mounted (see
                            tournamentSearchOptionsBus), so a plain typed
                            query still works as free text everywhere, while
                            Участники/Рейтинг additionally offer player-name
                            chips and Жеребкування also offers group/bracket
                            chips. */}
                        <div className="tournament-search-container">
                            <FeatherIcon icon="search" size={16} className="tournament-search-icon" />
                            <CreatableSelect
                                autoFocus
                                isMulti
                                options={searchOptions}
                                value={searchChips}
                                inputValue={searchInputValue}
                                onInputChange={(val) => setSearchInputValue(val)}
                                onChange={setSearchChips}
                                // Deferred one tick, same reasoning as
                                // ScheduleTable's own onBlur - a menu-option
                                // click blurs the control a beat before the
                                // click itself registers, so closing
                                // synchronously here would unmount the menu
                                // mid-click and drop the pick.
                                onBlur={() => setTimeout(() => {
                                    if (searchChips.length === 0 && !searchInputValue.trim()) setSearchOpen(false);
                                }, 150)}
                                placeholder={t("Search player by first name, last name")}
                                formatCreateLabel={(val) => `${t("Search text")}: "${val}"`}
                                classNamePrefix="react-select"
                                noOptionsMessage={() => t("No matching options")}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                onCreateOption={(v) => {
                                    setSearchChips(prev => [...prev, { value: `q_${v}`, label: v, type: 'q' }]);
                                    setSearchInputValue("");
                                }}
                            />
                        </div>
                    </li>
                </ul>
            )}
            <TournamentSwipeTabs tabs={visibleTabs} activeTab={activeTab} disabled={is_authorized}>
                {renderTabContent()}
                {/* Hoisted up here (was duplicated per-tab in TournamentGroups/
                    TournamentGrids/TournamentSchedule, missing entirely from
                    Information/Players/Rating) so every tab gets the exact same
                    bottom clearance above the fixed nav instead of only the three
                    tabs that happen to host a matches bottom sheet. */}
                {!isPreviewFrame() && <div className="bottom-sheet-page-spacer" />}
            </TournamentSwipeTabs>
        </Container>
    );
};

export default Tournament;
